import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL;

const TaskAdd = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");
  const [isUsersLoading, setIsUsersLoading] = useState(true);
  const [formData, setFormData] = useState({
    taskDescription: "",
    expectedCompletedDate: "",
    assignedBy: "",
    assignedTo: "",
    taskStatus: "Pending",
    priority: "Medium",
    userRemarks: "",

    taskType: "one_time",
    reportType: "",
    weeklyDates: [],
    monthlyDate: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  // Get current user info
  useEffect(() => {
    const role = localStorage.getItem("role");
    const userId = localStorage.getItem("id");
    const userName = localStorage.getItem("name");

    if (role) {
      setCurrentUserRole(role.toLowerCase());
    }
    if (userId) {
      setFormData((prev) => ({
        ...prev,
        assignedBy: userId,
      }));
    }
    if (userName) {
      setCurrentUserName(userName);
    }
  }, []);

  // Fetch users for dropdowns
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsUsersLoading(true);

        const payload = {
          action: "get-users",
          page: 1,
          limit: 1000,
        };
        const response = await axios.post(API_URL, payload);

        let responseData = response.data;
        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }
        

        if (responseData.success === true && responseData.users) {
          setUsers(responseData.users);
        } else {
          toast.error(responseData.message || "Failed to fetch users", {
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Fetch users error:", error);
        toast.error("Failed to load users", { autoClose: 2000 });
      } finally {
        setIsUsersLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Generic change handler
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "weeklyDates") {
      setFormData((prev) => ({
        ...prev,
        weeklyDates: value
          .split(",")
          .map((date) => date.trim())
          .filter(Boolean),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleWeeklyDateChange = (index, value) => {
    const dates = [...formData.weeklyDates];
    dates[index] = value;

    setFormData((prev) => ({
      ...prev,
      weeklyDates: dates,
    }));
  };

  // Simple form validation
  const validateForm = () => {
    if (!formData.taskDescription.trim()) {
      toast.error("Task description is required", { autoClose: 2000 });
      return false;
    }
    if (!formData.expectedCompletedDate) {
      toast.error("Expected completion date is required", { autoClose: 2000 });
      return false;
    }
    if (!formData.assignedBy) {
      toast.error("Assigned by is required", { autoClose: 2000 });
      return false;
    }
    if (!formData.assignedTo) {
      toast.error("Assigned to is required", { autoClose: 2000 });
      return false;
    }
    if (formData.taskType === "repeated") {
      if (!formData.reportType) {
        toast.error("Report Type is required");
        return false;
      }

      if (
        formData.reportType === "weekly" &&
        formData.weeklyDates.filter(Boolean).length === 0
      ) {
        toast.error("Please select weekly dates");
        return false;
      }

      if (formData.reportType === "monthly" && !formData.monthlyDate) {
        toast.error("Please select monthly date");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    if (formData.taskType === "repeated" && formData.reportType === "weekly") {
      const dates = formData.weeklyDates.filter(Boolean);

      if (new Set(dates).size !== dates.length) {
        toast.error("Duplicate weekly dates are not allowed");
        return;
      }
    }

    setIsLoading(true);

    try {
      const formatDate = (date) => {
        const [year, month, day] = date.split("-");
        return `${day}-${month}-${year}`;
      };

      const payload = {
        action: "create-task",
        taskDescription: formData.taskDescription.trim(),
        expectedCompletedDate: formData.expectedCompletedDate,
        assignedBy: formData.assignedBy,
        assignedTo: formData.assignedTo,
        taskStatus: formData.taskStatus,
        priority: formData.priority,
        userRemarks: formData.userRemarks.trim(),
        taskType: formData.taskType,
        reportType:
          formData.taskType === "repeated" ? formData.reportType : null,
        weeklyDates:
          formData.taskType === "repeated" && formData.reportType === "weekly"
            ? formData.weeklyDates
                .filter(Boolean)
                .map((date) => formatDate(date))
            : [],

        monthlyDate:
          formData.taskType === "repeated" && formData.reportType === "monthly"
            ? formatDate(formData.monthlyDate)
            : null,

        createdBy: currentUserId,
      };

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;
      console.log("FULL RESPONSE:", response.data);
      console.log("TASK DATA:", response.data.data);
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (responseData.success === true) {
        toast.success(responseData.message || "Task created successfully", {
          autoClose: 2000,
        });

        setTimeout(() => {
          navigate("/task");
        }, 2000);
      } else {
        toast.error(responseData.message || "Failed to create task", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Create task error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message || "An error occurred while creating task",
          {
            autoClose: 2000,
          },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          {
            autoClose: 2000,
          },
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Create Task</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/task">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Task Description
                        </label>
                        <div className="col-12 col-md-8">
                          <textarea
                            name="taskDescription"
                            className="form-control"
                            placeholder="Enter task description"
                            rows="3"
                            value={formData.taskDescription}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Expected Completion Date
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="date"
                            name="expectedCompletedDate"
                            className="form-control"
                            value={formData.expectedCompletedDate}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Task Type
                        </label>

                        <div className="col-12 col-md-8">
                          <select
                            name="taskType"
                            className="form-control"
                            value={formData.taskType}
                            onChange={handleChange}
                          >
                            <option value="one_time">One Time</option>
                            <option value="repeated">Repeated</option>
                          </select>
                        </div>
                      </div>
                      {formData.taskType === "repeated" && (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Report Type
                          </label>

                          <div className="col-12 col-md-8">
                            <select
                              name="reportType"
                              className="form-control"
                              value={formData.reportType}
                              onChange={handleChange}
                            >
                              <option value="">Select</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </select>
                          </div>
                        </div>
                      )}
                      {formData.taskType === "repeated" &&
                        formData.reportType === "weekly" && (
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Weekly Dates
                            </label>

                            <div className="col-12 col-md-8">
                              {[0, 1, 2, 3].map((item) => (
                                <input
                                  key={item}
                                  type="date"
                                  className="form-control mb-2"
                                  value={formData.weeklyDates[item] || ""}
                                  onChange={(e) =>
                                    handleWeeklyDateChange(item, e.target.value)
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      {formData.taskType === "repeated" &&
                        formData.reportType === "monthly" && (
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Monthly Date
                            </label>

                            <div className="col-12 col-md-8">
                              <input
                                type="date"
                                name="monthlyDate"
                                className="form-control"
                                value={formData.monthlyDate}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        )}
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Priority
                        </label>

                        <div className="col-12 col-md-8">
                          <select
                            name="priority"
                            className="form-control"
                            value={formData.priority}
                            onChange={handleChange}
                          >
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Assigned By
                        </label>
                        <div className="col-12 col-md-8">
                          {currentUserRole === "admin" ? (
                            <div style={{ position: "relative" }}>
                              <select
                                name="assignedBy"
                                className="form-control"
                                value={formData.assignedBy}
                                onChange={handleChange}
                                disabled={isUsersLoading}
                                required
                              >
                                <option value="">
                                  {isUsersLoading
                                    ? "Loading users..."
                                    : "Select User"}
                                </option>

                                {users.map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {user.name}
                                  </option>
                                ))}
                              </select>

                              {isUsersLoading && (
                                <div
                                  style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                  }}
                                >
                                  <i className="fa fa-spinner fa-spin text-muted"></i>
                                </div>
                              )}
                            </div>
                          ) : (
                            <input
                              type="text"
                              className="form-control"
                              value={currentUserName}
                              disabled
                              readOnly
                            />
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Assigned To
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            name="assignedTo"
                            className="form-control"
                            value={formData.assignedTo}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select User</option>
                            {users.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Task Status
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            name="taskStatus"
                            className="form-control"
                            value={formData.taskStatus}
                            onChange={handleChange}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Inprogress">Inprogress</option>
                            <option value="Completed">Completed</option>
                            <option value="Hold">Hold</option>
                          </select>
                        </div>
                      </div>

                      {/* <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Task Duration</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="taskDuration"
                            className="form-control"
                            placeholder="e.g., 2 days, 1 week"
                            value={formData.taskDuration}
                            onChange={handleChange}
                          />
                        </div>
                      </div> */}

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          User Remarks
                        </label>
                        <div className="col-12 col-md-8">
                          <textarea
                            name="userRemarks"
                            className="form-control"
                            placeholder="Enter any remarks or notes"
                            rows="3"
                            value={formData.userRemarks}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isLoading}
                      >
                        {isLoading ? "Creating..." : "Create"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskAdd;
